import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

export type Lang = "fr" | "en";

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: <T = string>(dict: Record<Lang, T>) => T;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const STORAGE_KEY = "elfaza_lang";

function detectInitialLang(pathname: string): Lang {
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  if (typeof window === "undefined") return "fr";
  const stored = localStorage.getItem(STORAGE_KEY) as Lang | null;
  if (stored === "fr" || stored === "en") return stored;
  const nav = navigator.language?.toLowerCase() ?? "";
  if (nav.startsWith("en")) return "en";
  return "fr";
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang(location.pathname));

  // Sync lang with URL changes (e.g., user navigates to /en or back to /)
  useEffect(() => {
    const fromUrl: Lang | null =
      location.pathname === "/en" || location.pathname.startsWith("/en/") ? "en" : "fr";
    if (fromUrl !== lang) setLangState(fromUrl);
    document.documentElement.lang = fromUrl === "en" ? "en" : "fr";
  }, [location.pathname, lang]);

  const setLang = (next: Lang) => {
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
    document.documentElement.lang = next === "en" ? "en" : "fr";
  };

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      setLang,
      t: (dict) => {
        // Runtime safety net: if the requested locale is missing/empty,
        // fall back to the other locale, then to any non-empty value.
        // TypeScript already enforces both keys at compile time — this guards
        // against runtime gaps (e.g. dynamic dicts, missing JSX children).
        const isEmpty = (v: unknown) =>
          v === undefined ||
          v === null ||
          (typeof v === "string" && v.trim().length === 0);

        const primary = dict[lang];
        if (!isEmpty(primary)) return primary;

        const other: Lang = lang === "fr" ? "en" : "fr";
        const secondary = dict[other];
        if (!isEmpty(secondary)) {
          if (typeof console !== "undefined") {
            console.warn(
              `[i18n] Missing "${lang}" translation, falling back to "${other}".`,
              dict,
            );
          }
          return secondary;
        }

        // Last-resort fallback: empty string cast to expected type.
        if (typeof console !== "undefined") {
          console.warn("[i18n] Both locales empty, returning empty fallback.", dict);
        }
        return "" as unknown as typeof primary;
      },
    }),
    [lang],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}

/**
 * Toggle between FR and EN equivalent URLs.
 * /         <-> /en
 * /contact  <-> /en/contact
 */
export function buildLocalizedPath(currentPath: string, target: Lang): string {
  const isEn = currentPath === "/en" || currentPath.startsWith("/en/");
  if (target === "en") {
    if (isEn) return currentPath;
    if (currentPath === "/") return "/en";
    return `/en${currentPath}`;
  }
  // target === "fr"
  if (!isEn) return currentPath;
  if (currentPath === "/en") return "/";
  return currentPath.replace(/^\/en/, "") || "/";
}
